import React, {createContext, useContext, useState, useEffect, ReactNode, useCallback} from "react";
import {useNavigate, useLocation, Navigate, Location} from "react-router-dom";
import {jwtDecode} from "jwt-decode";
import {
  login as apiLogin,
  googleLogin as apiGoogleLogin,
  logout as apiLogout,
  getCurrentUser,
  refreshToken as apiRefreshToken,
  LoginResponse,
  RefreshTokenResponse,
} from "../api/auth";
import {User, UserRole} from "../api/user";
import {ErrorBoundary} from "../utils/errorHandler";

// Session timeout constants
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const REFRESH_TOKEN_THRESHOLD = 5 * 60 * 1000; // 5 minutes before token expires
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];

// Types
interface TokenData {
  exp: number;
  iat: number;
  userId: string;
  email: string;
  role: UserRole;
  tenantId: string;
}

interface AuthProviderProps {
  children: ReactNode;
  sessionTimeout?: number;
  onSessionTimeout?: () => void;
}

export interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: UserRole | UserRole[];
  fallback?: ReactNode;
  redirectTo?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleSignIn: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  isAuthenticated: boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  authError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let refreshTokenRequest: Promise<boolean> | null = null;

// Using imported ErrorBoundary from errorHandler

// Session Timeout Hook
const useSessionTimeout = (timeout: number, onTimeout: () => void) => {
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(onTimeout, timeout);
    };

    // Set initial timeout
    resetTimeout();

    // Add event listeners for user activity
    const events = ACTIVITY_EVENTS.map((event) => window.addEventListener(event, resetTimeout, false));

    return () => {
      clearTimeout(timeoutId);
      ACTIVITY_EVENTS.forEach((event, index) => window.removeEventListener(event, events[index] as any, false));
    };
  }, [timeout, onTimeout]);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({children, sessionTimeout = SESSION_TIMEOUT, onSessionTimeout}) => {
  // Handle authentication errors from error boundaries and auth operations
  const handleAuthError = useCallback((error: Error, info?: React.ErrorInfo) => {
    console.error("Auth Error:", error, info);
    const message = error.message || "An authentication error occurred";
    setAuthError(message);

    // Log to error tracking service (e.g., Sentry)
    if (process.env.NODE_ENV === "production") {
      // Sentry.captureException(error, { extra: { info } });
    }

    return message;
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Reset the inactivity timer
    // This is a placeholder - implement your actual inactivity timer logic here
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const clearAuth = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    setUser(null);
    setAuthError(null);
  }, []);

  // Handle session timeout
  const handleSessionTimeout = useCallback(() => {
    clearAuth();
    if (onSessionTimeout) {
      onSessionTimeout();
    } else {
      navigate("/login", {
        state: {
          sessionTimeout: true,
          from: location.pathname,
        },
      });
    }
  }, [clearAuth, navigate, onSessionTimeout, location]);

  // Setup session timeout
  useSessionTimeout(sessionTimeout, handleSessionTimeout);

  const setAuthData = useCallback((token: string, refreshToken: string, userData: User) => {
    localStorage.setItem("token", token);
    localStorage.setItem("refresh_token", refreshToken);
    setUser(userData);
    setAuthError(null);
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (refreshTokenRequest) {
      return refreshTokenRequest;
    }

    try {
      refreshTokenRequest = (async () => {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        const response = await apiRefreshToken(refreshToken);
        if (!response.access_token || !response.refresh_token || !response.user) {
          throw new Error("Invalid refresh token response");
        }

        setAuthData(response.access_token, response.refresh_token, response.user);
        return true;
      })();

      return await refreshTokenRequest;
    } catch (error) {
      console.error("Token refresh failed:", error);
      clearAuth();
      return false;
    } finally {
      refreshTokenRequest = null;
    }
  }, [clearAuth, setAuthData]);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      // Check token expiration
      const decoded = jwtDecode<TokenData>(token);
      const now = Date.now() / 1000;
      const expiresIn = decoded.exp - now;

      if (expiresIn < 0) {
        // Token expired, try to refresh
        const refreshed = await refreshToken();
        if (!refreshed) {
          throw new Error("Session expired. Please log in again.");
        }
      } else if (expiresIn < REFRESH_TOKEN_THRESHOLD / 1000) {
        // Token will expire soon, refresh in background
        refreshToken().catch(console.error);
      }

      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error("Auth check failed:", error);
      clearAuth();
      setAuthError(error instanceof Error ? error.message : "Authentication failed");
      throw error;
    } finally {
      setLoading(false);
    }
  }, [clearAuth, refreshToken]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      try {
        setLoading(true);
        setAuthError(null);

        // Call the login API
        const response = await apiLogin(email, password);
        const {access_token, refresh_token, user} = response;

        console.log("Login successful - Token received:", access_token ? "Yes" : "No");
        console.log("Login successful - Token length:", access_token?.length);
        console.log("Login successful - Token preview:", access_token?.substring(0, 20) + "...");

        if (!access_token || !refresh_token) {
          throw new Error("Authentication failed: No tokens received");
        }

        // Store tokens with consistent keys
        localStorage.setItem("token", access_token);
        localStorage.setItem("refresh_token", refresh_token);

        console.log("Login - Tokens stored in localStorage");
        console.log("Login - Stored token length:", localStorage.getItem("token")?.length);
        console.log("Login - Stored token preview:", localStorage.getItem("token")?.substring(0, 20) + "...");

        // Set user data
        setUser(user);
        resetInactivityTimer();

        // Navigate to dashboard or intended URL
        const from = location.state?.from?.pathname || "/";
        if (!user.tenantId) {
          navigate("/onboarding", {replace: true});
        } else {
          navigate(from, {replace: true});
        }
      } catch (error) {
        const appError = new Error(error instanceof Error ? error.message : "Login failed. Please check your credentials.");
        setAuthError(appError.message);
        throw appError;
      } finally {
        setLoading(false);
      }
    },
    [navigate, resetInactivityTimer]
  );

  const googleSignIn = useCallback(
    async (token: string): Promise<void> => {
      try {
        setLoading(true);
        setAuthError(null);

        const response = await apiGoogleLogin(token);
        const {access_token, refresh_token, user} = response;

        if (!access_token || !refresh_token) {
          throw new Error("Authentication failed: No tokens received");
        }

        // Store tokens
        localStorage.setItem("token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        
        // Set user data
        setUser(user);
        resetInactivityTimer();

        // Navigate
        const from = location.state?.from?.pathname || "/";
        if (!user.tenantId) {
          navigate("/onboarding", {replace: true});
        } else {
          navigate(from, {replace: true});
        }
      } catch (error) {
        const appError = new Error("Google Login failed");
        setAuthError(appError.message);
        throw appError;
      } finally {
        setLoading(false);
      }
    },
    [navigate, resetInactivityTimer]
  );


  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      clearAuth();
      navigate("/login");
    }
  };

  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!user?.role) return false;
      return Array.isArray(role) ? role.includes(user.role) : user.role === role;
    },
    [user]
  );

  const contextValue = {
    user,
    loading,
    login,
    googleSignIn,
    logout,
    refreshToken,
    isAuthenticated: !!user,
    hasRole,
    authError,
  };

  // Fallback UI for error boundary
  const fallbackUI = (
    <div>
      <h2>Something went wrong</h2>
      {authError && <p>{authError}</p>}
      <button onClick={() => window.location.reload()}>Reload Page</button>
    </div>
  );

  return (
    <ErrorBoundary
      fallback={fallbackUI}
      onError={(error, info) => {
        console.error("Auth Error Boundary caught:", error, info);
        setAuthError(error.message);
      }}>
      <AuthContext.Provider value={contextValue}>
        {loading ? (
          <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "100vh"}}>
            <div>Loading...</div>
          </div>
        ) : (
          children
        )}
      </AuthContext.Provider>
    </ErrorBoundary>
  );
};

// Protected Route Component
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children, requiredRoles = [], fallback = null, redirectTo = "/login"}) => {
  const {isAuthenticated, hasRole, loading} = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>; // Or your custom loading component
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{from: location}} replace />;
  }

  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    return fallback ? <>{fallback}</> : <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
